import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(1);

  const faqs = [
    {
      id: 1,
      question: "How does your pricing work?",
      answer: `Our pricing is completely transparent with no hidden fees. We offer three main pricing tiers:\n\n• **Starter (₹299)**: Perfect for small projects with basic requirements\n• **Professional (₹599)**: Most popular for growing businesses with comprehensive needs\n• **Enterprise (₹1,299)**: For large-scale projects requiring premium service\n\nEach tier includes different levels of support, revisions, and delivery timelines. You can also use our pricing calculator to get a custom estimate based on your specific project requirements.`,
      category: "pricing"
    },
    {
      id: 2,
      question: "What\'s included in unlimited revisions?",
      answer: `Unlimited revisions means we'll keep refining your project until you're 100% satisfied. This includes:\n\n• **Design changes**: Color schemes, layouts, typography adjustments\n• **Content updates**: Text changes, image replacements, copy refinements\n• **Functionality tweaks**: Feature adjustments, user experience improvements\n• **Performance optimizations**: Speed improvements, mobile responsiveness fixes\n\nThe only limitation is that revisions must stay within the original project scope. Major scope changes would require a separate quote, but we'll always discuss this with you first.`,
      category: "revisions"
    },
    {
      id: 3,
      question: "How do you ensure quality and reliability?",
      answer: `Quality and reliability are our top priorities. Here's how we ensure both:\n\n**Quality Assurance:**\n• 5-step vetting process for all freelancers\n• Portfolio verification and skill testing\n• Client reference checks and background verification\n• Ongoing performance monitoring and feedback integration\n\n**Reliability Measures:**\n• Dedicated project managers for every project\n• Milestone-based tracking with regular updates\n• 24/7 support team available for questions\n• Money-back guarantee if you're not satisfied\n• Escrow payment protection for your security`,
      category: "quality"
    },
    {
      id: 4,
      question: "What happens if I'm not satisfied with the work?",
      answer: `Your satisfaction is guaranteed. If you're not happy with the final result, here's what we offer:\n\n**100% Money-Back Guarantee:**\n• Full refund within 30 days if you're not satisfied\n• No questions asked policy for peace of mind\n• Partial refunds available for partially completed work\n\n**Before Refund Options:**\n• Unlimited revisions to get it right\n• Option to work with a different freelancer\n• Project scope adjustments if needed\n• Direct communication with our quality assurance team\n\nWe've maintained a 98% client satisfaction rate because we work tirelessly to exceed expectations.`,
      category: "guarantee"
    },
    {
      id: 5,
      question: "How long does a typical project take?",
      answer: `Project timelines vary based on complexity and scope:\n\n**Typical Timeframes:**\n• **Logo Design**: 3-5 days\n• **Website Development**: 7-14 days\n• **Mobile App**: 14-21 days\n• **E-commerce Store**: 14-21 days\n• **Branding Package**: 7-10 days\n• **Marketing Campaign**: 5-7 days\n\n**Rush Options Available:**\n• 50% faster delivery for urgent projects\n• 24/7 development for critical deadlines\n• Priority queue placement\n\n**Factors Affecting Timeline:**\n• Project complexity and feature requirements\n• Number of revisions requested\n• Client feedback and approval speed\n• Third-party integrations needed`,
      category: "timeline"
    },
  ];

  const categories = [
    { id: 'all', label: 'All Questions', count: faqs.length },
    { id: 'pricing', label: 'Pricing', count: faqs.filter(f => f.category === 'pricing').length },
    { id: 'quality', label: 'Quality', count: faqs.filter(f => f.category === 'quality').length },
    { id: 'timeline', label: 'Timeline', count: faqs.filter(f => f.category === 'timeline').length },
    { id: 'support', label: 'Support', count: faqs.filter(f => f.category === 'support').length }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const formatAnswer = (answer) => {
    return answer.split('\n').map((line, index) => {
      if (line.startsWith('• **') && line.includes('**:')) {
        const [boldPart, normalPart] = line.split('**:');
        return (
          <div key={index} className="flex items-start mb-2">
            <Icon name="ArrowRight" size={14} className="text-blue-600 mr-2 mt-1 flex-shrink-0" />
            <span>
              <strong>{boldPart.replace('• **', '')}</strong>: {normalPart}
            </span>
          </div>
        );
      } else if (line.startsWith('**') && line.endsWith('**')) {
        return (
          <h4 key={index} className="font-semibold text-gray-900 mt-4 mb-2">
            {line.replace(/\*\*/g, '')}
          </h4>
        );
      } else if (line.startsWith('• ')) {
        return (
          <div key={index} className="flex items-start mb-1">
            <Icon name="Check" size={14} className="text-green-600 mr-2 mt-1 flex-shrink-0" />
            <span>{line.replace('• ', '')}</span>
          </div>
        );
      } else if (line.trim() === '') {
        return <div key={index} className="mb-2"></div>;
      } else {
        return (
          <p key={index} className="mb-2">
            {line}
          </p>
        );
      }
    });
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-poppins text-gray-900 mb-6">
            Frequently Asked
            <span className="text-blue-600"> Questions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to the most common questions about our services, pricing, and process. Can't find what you're looking for? Contact us directly.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center px-4 py-2 rounded-full font-medium transition-all duration-300 ₹{
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category.label}
              <span className={`ml-2 px-2 py-1 rounded-full text-xs ₹{
                selectedCategory === category.id
                  ? 'bg-white bg-opacity-20 text-white' :'bg-gray-100 text-gray-600'
              }`}>
                {category.count}
              </span>
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {filteredFAQs.map((faq) => (
            <div
              key={faq.id}
              className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold font-poppins text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <div className={`w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ₹{
                  openFAQ === faq.id ? 'rotate-180' : ''
                }`}>
                  <Icon 
                    name="ChevronDown" 
                    size={20} 
                    className="text-blue-600" 
                  />
                </div>
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ₹{
                openFAQ === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  {formatAnswer(faq.answer)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-blue-600 text-white rounded-3xl p-8">
            <h3 className="text-2xl font-bold font-poppins mb-4">
              Still Have Questions?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Our team is here to help. Get in touch and we'll provide personalized answers to any questions about your specific project needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300 inline-flex items-center justify-center">
                <Icon name="MessageCircle" size={20} className="mr-2" />
                Live Chat Support
              </button>
              <button className="bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-800 transition-colors duration-300 inline-flex items-center justify-center">
                <Icon name="Phone" size={20} className="mr-2" />
                Schedule a Call
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;